import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Chip,
  Divider,
  LinearProgress,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { 
  Code as CodeIcon, 
  Functions as FunctionsIcon, 
  SmartToy as SmartToyIcon,
  Terminal as TerminalIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  Schema as SchemaIcon,
  DataObject as DataObjectIcon
} from '@mui/icons-material';
import { playlistAgentTools } from '../../services/playlistAgentSchema';
import { searchAgentTools } from '../../services/searchAgentSchema';

interface AgentResponseProps {
  command: string;
  functionCalled: string;
  parameters: Record<string, any>;
  isVisible: boolean;
  isProcessing?: boolean;
  onClose?: () => void;
  openaiResponse?: string;
}

const AgentResponse: React.FC<AgentResponseProps> = ({ 
  command, 
  functionCalled, 
  parameters,
  isVisible,
  isProcessing = false,
  onClose,
  openaiResponse
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  if (!isVisible) return null;

  // Combine tools from both agents to find the correct schema
  const allTools = [...playlistAgentTools, ...searchAgentTools];
  
  // Find the actual function schema
  const functionSchema = allTools.find(
    tool => tool.function.name === functionCalled
  )?.function || { name: functionCalled, description: "Function schema not found" };

  return (
    <Paper sx={{ 
      p: isMobile ? 1.5 : 2, 
      mb: 3, 
      bgcolor: 'background.default', 
      border: 1, 
      borderColor: 'primary.light',
      borderRadius: 2,
      boxShadow: 3,
      overflow: 'hidden' // Prevent content overflow
    }}
    className="agent-response-container">
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        mb: 2,
        flexWrap: isMobile ? 'wrap' : 'nowrap'
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          width: isMobile ? '100%' : 'auto',
          mb: isMobile ? 1 : 0
        }}>
          <SmartToyIcon sx={{ mr: 1, color: 'primary.main', fontSize: isMobile ? 24 : 28 }} />
          <Typography variant={isMobile ? "subtitle1" : "h6"} color="primary.main" noWrap>
            Function Calling Agent
          </Typography>
        </Box>
        
        {onClose && (
          <Box sx={{ ml: 'auto' }}>
            <Tooltip title="Clear">
              <IconButton onClick={onClose} size={isMobile ? "medium" : "small"} 
                sx={{ minWidth: isMobile ? '44px' : 'auto', minHeight: isMobile ? '44px' : 'auto' }}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
      
      {isProcessing ? (
        <LinearProgress sx={{ mb: 2 }} />
      ) : (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: 2, 
          p: 0.5, 
          bgcolor: 'success.light', 
          borderRadius: 1,
          color: 'success.contrastText'
        }}>
          <CheckCircleIcon sx={{ mr: 1, fontSize: 16 }} />
          <Typography variant="body2">
            Function executed successfully
          </Typography>
        </Box>
      )}
      
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <TerminalIcon sx={{ mr: 1, color: 'text.secondary' }} />
          <Typography variant="subtitle2" color="text.secondary">
            Processing Command:
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ 
          fontFamily: 'monospace', 
          mb: 1, 
          p: isMobile ? 1 : 1.5, 
          bgcolor: 'background.paper',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          fontSize: isMobile ? '0.8rem' : '0.875rem',
          overflowX: 'auto',
          wordBreak: 'break-word'
        }}
        className="command-display">
          "{command}"
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <FunctionsIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="subtitle2">
            Function Called:
          </Typography>
        </Box>
        <Chip 
          label={functionSchema.name} 
          color="primary" 
          variant="outlined" 
          sx={{ 
            fontFamily: 'monospace', 
            fontWeight: 'bold',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }} 
        />
        
        {/* Function Schema */}
        <Accordion sx={{ mt: 1, bgcolor: 'background.paper' }} className="mobile-accordion">
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{ 
              minHeight: isMobile ? '48px' : '56px',
              '& .MuiAccordionSummary-content': {
                margin: isMobile ? '8px 0' : '12px 0'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SchemaIcon sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                Function Schema
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: isMobile ? 1 : 2 }}>
            <Box 
              sx={{ 
                p: isMobile ? 0.5 : 1, 
                bgcolor: 'background.default', 
                borderRadius: 1, 
                fontFamily: 'monospace',
                fontSize: isMobile ? '0.65rem' : '0.75rem',
                overflowX: 'auto',
                border: '1px solid',
                borderColor: 'divider'
              }}
              className="code-container"
            >
              <pre style={{ margin: 0, overflowX: 'auto', whiteSpace: isMobile ? 'pre-wrap' : 'pre' }}>
                {JSON.stringify(functionSchema, null, 2)}
              </pre>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <CodeIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="subtitle2">
            Parameters:
          </Typography>
        </Box>
        <Box 
          sx={{ 
            p: isMobile ? 1 : 1.5, 
            bgcolor: 'background.paper', 
            borderRadius: 1, 
            fontFamily: 'monospace',
            fontSize: isMobile ? '0.75rem' : '0.875rem',
            overflowX: 'auto',
            border: '1px solid',
            borderColor: 'divider'
          }}
          className="code-container"
        >
          <pre style={{ margin: 0, overflowX: 'auto', whiteSpace: isMobile ? 'pre-wrap' : 'pre' }}>
            {JSON.stringify(parameters, null, 2) || "{}"}
          </pre>
        </Box>
      </Box>

      {/* OpenAI Response */}
      {openaiResponse && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <SmartToyIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="subtitle2">
              OpenAI Response:
            </Typography>
          </Box>
          
          {/* Try to parse as JSON for better display */}
          {(() => {
            try {
              // Check if the response is JSON
              const jsonResponse = JSON.parse(openaiResponse);
              return (
                <Box 
                  sx={{ 
                    p: isMobile ? 1 : 1.5, 
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'primary.light',
                    fontFamily: 'monospace',
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    overflowX: 'auto'
                  }}
                  className="code-container"
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <DataObjectIcon sx={{ mr: 1, color: 'success.main', fontSize: 18 }} />
                    <Typography variant="caption" color="success.main">
                      Valid JSON Response
                    </Typography>
                  </Box>
                  <pre style={{ margin: 0, overflowX: 'auto', whiteSpace: isMobile ? 'pre-wrap' : 'pre' }}>
                    {JSON.stringify(jsonResponse, null, 2)}
                  </pre>
                </Box>
              );
            } catch (e) {
              // If not JSON, display as plain text
              return (
                <Typography variant="body1" sx={{ 
                  p: isMobile ? 1 : 1.5, 
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  whiteSpace: 'pre-wrap',
                  fontSize: isMobile ? '0.8rem' : '0.875rem',
                  wordBreak: 'break-word'
                }}
                className="text-response">
                  {openaiResponse}
                </Typography>
              );
            }
          })()}
        </Box>
      )}
    </Paper>
  );
};

export default AgentResponse;
